// iter622 — Cortisol-Thyroid Axis card.
// Circuit-level coaching: explains rT3 shunt when chronic cortisol meets
// upper-normal TSH. Reusable on PassiveStressBanner + ThyroidTwinPage.
import React, { useState } from "react";
import { useTheme } from "@/theme/ThemeContext";

export default function CortisolThyroidAxisCard({ axis, defaultCollapsed = false }) {
  const { isDark } = useTheme();
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  if (!axis || !axis.detected) return null;

  const ev = axis.evidence || {};
  const supplements = axis.supplements_bg || [];
  const nextSteps = axis.next_steps_bg || [];

  const baseCls = isDark
    ? "bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border-purple-500/40 text-purple-50"
    : "bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-300 text-purple-950";

  return (
    <section
      data-testid="cortisol-thyroid-axis-card"
      className={`rounded-2xl border-2 p-4 sm:p-5 ${baseCls}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        data-testid="axis-toggle"
        className="w-full flex items-start gap-3 text-left"
      >
        <span className="text-2xl shrink-0" aria-hidden="true">🧬</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold uppercase tracking-wider" data-testid="axis-title">
              {axis.title_bg || "Cortisol-Thyroid axis check"}
            </h3>
            <span className="text-[10px] opacity-70">
              {expanded ? "↑ свий" : "↓ разгъни"}
            </span>
          </div>
          <p className="text-xs mt-1 opacity-90">
            <strong>{ev.cortisol_consecutive_days} последователни дни</strong> с високо
            кортизолно натоварване + <strong>TSH {ev.tsh_miu_l} mIU/L</strong> ({ev.tsh_band})
            {ev.measured_on ? ` · измерено ${ev.measured_on}` : ""}
          </p>
        </div>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3" data-testid="axis-expanded">
          {/* Education */}
          <div
            className={`p-3 rounded-xl ${
              isDark ? "bg-purple-950/30" : "bg-white/60"
            }`}
            data-testid="axis-education"
          >
            <div className="text-[10px] uppercase font-bold opacity-70 mb-1">
              ✨ Как работи (T4 → T3 axis)
            </div>
            <p className="text-sm leading-relaxed">{axis.education_bg}</p>
          </div>

          {/* Supplements */}
          {supplements.length > 0 && (
            <div
              className={`p-3 rounded-xl ${
                isDark ? "bg-purple-950/30" : "bg-white/60"
              }`}
              data-testid="axis-supplements"
            >
              <div className="text-[10px] uppercase font-bold opacity-70 mb-2">
                💊 Препоръчани добавки (3 месеца)
              </div>
              <ul className="space-y-2">
                {supplements.map((s, i) => (
                  <li
                    key={i}
                    data-testid={`axis-supplement-${i}`}
                    className={`p-2 rounded-lg text-xs ${
                      isDark ? "bg-purple-900/30" : "bg-purple-100/60"
                    }`}
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <strong className="text-sm">{s.name_bg}</strong>
                      <span className="opacity-80 font-mono">{s.dose_bg}</span>
                    </div>
                    <p className="opacity-80 mt-0.5">
                      <em>Защо:</em> {s.mechanism_bg}
                    </p>
                    {s.form_bg && (
                      <p className="opacity-70 mt-0.5">
                        <em>Форма:</em> {s.form_bg}
                      </p>
                    )}
                    {s.duration_bg && (
                      <p className="opacity-70 mt-0.5">
                        <em>Продължителност:</em> {s.duration_bg}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Next steps */}
          {nextSteps.length > 0 && (
            <div
              className={`p-3 rounded-xl ${
                isDark ? "bg-purple-950/30" : "bg-white/60"
              }`}
              data-testid="axis-next-steps"
            >
              <div className="text-[10px] uppercase font-bold opacity-70 mb-2">
                ➜ Следващи стъпки
              </div>
              <ul className="space-y-1.5 text-xs">
                {nextSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`axis-step-${i}`}>
                    <span className="opacity-60 shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          {axis.disclaimer_bg && (
            <p
              className={`text-[11px] italic opacity-75 ${isDark ? "text-purple-200" : "text-purple-900"}`}
              data-testid="axis-disclaimer"
            >
              ⚠️ {axis.disclaimer_bg}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
